import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Button, Typography, Box, Tabs, Tab, Snackbar, Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ProjectFormFields from '../components/ProjectFormFields';
import { fetchProject, fetchCompanies, fetchLocations, fetchProjectTypes, saveProject, deleteProject } from '../api/ProjectApi';
import ProjectAssets from './ProjectAssets';
import KanbanBoard from '../components/KanbanBoard';
import ProjectTeams from '../components/ProjectTeams';
import ProjectDocuments from '../components/ProjectDocuments';
import ProjectTimeline from '../components/ProjectTimeline';
import { formatDate, formatNumber } from '../utils/formatterUtils'; // Import format utilities

const ProjectEdit = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState({
    company_id: { id: '', companyName: '' },
    location_id: { id: '', locationName: '' },
    project_name: '',
    start_date: null,
    planned_completion_date: null,
    actual_completion_date: null,
    status: '',
    estimated_cost: '',
    actual_cost: '',
    description: '',
    project_manager: '',
    client_name: '',
    priority: '',
    risk_level: '',
    budget: '',
    actual_hours: '',
    estimated_hours: '',
    milestones: '',
    deliverables: '',
    project_type: '',
  });
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      if (projectId !== 'new') {
        const projectData = await fetchProject(projectId);
        setProject(projectData);
      }
      const companiesData = await fetchCompanies();
      setCompanies(companiesData);
      const locationsData = await fetchLocations();
      console.log('Fetched locations in ProjectEdit:', locationsData); // Verify the fetched data
      setLocations(locationsData);
      const projectTypesData = await fetchProjectTypes();
      console.log('Fetched project types in ProjectEdit:', projectTypesData); // Verify the fetched data
      setProjectTypes(projectTypesData);
    };
    fetchData();
  }, [projectId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProject({ ...project, [name]: value });
  };

  const handleCompanyChange = (event, newValue) => {
    console.log('Selected Company:', newValue); // Debug statement
    setProject({
      ...project,
      company_id: newValue
        ? { companyId: newValue.companyId, companyName: newValue.companyName } // Ensure correct structure
        : { companyId: '', companyName: '' },
    });
  };

  const handleLocationChange = (event, newValue) => {
    setProject({ ...project, location_id: newValue ? { id: newValue.id, locationName: newValue.locationName } : { id: '', locationName: '' } });
  };

  const handleSave = async () => {
    try {
      const newProjectId = await saveProject(projectId, project);
      if (newProjectId) {
        navigate(`/project-edit/${newProjectId}`);
      }
      setSnackbarMessage('Project saved successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error saving project:', error);
      setSnackbarMessage('Failed to save project. Please try again later.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleNewProject = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleCreateNewProject = (saveChanges) => {
    if (saveChanges) {
      handleSave();
    }
    setProject({
      company_id: { id: '', companyName: '' },
      location_id: { id: '', locationName: '' },
      project_name: '',
      start_date: null,
      planned_completion_date: null,
      actual_completion_date: null,
      status: '',
      estimated_cost: '',
      actual_cost: '',
      description: '',
      project_manager: '',
      client_name: '',
      priority: '',
      risk_level: '',
      budget: '',
      actual_hours: '',
      estimated_hours: '',
      milestones: '',
      deliverables: '',
      project_type: '',
    });
    navigate('/project-edit/new');
    setDialogOpen(false);
  };

  const handleDelete = async () => {
    try {
      await deleteProject(projectId);
      alert('Project deleted successfully');
      navigate('/projects');
    } catch (error) {
      console.error('Error deleting project:', error);
      setSnackbarMessage('Failed to delete project. Please try again later.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  return (
    <Container>
      <Tabs value={tabIndex} onChange={handleTabChange} aria-label="project tabs">
        <Tab label="Project Details" sx={{ fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.87)' }} />
        <Tab label="Milestones" sx={{ fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.87)' }} />
        <Tab label="Timeline" sx={{ fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.87)' }} />
        <Tab label="Project Team" sx={{ fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.87)' }} />
        <Tab label="Project Documents" sx={{ fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.87)' }} />
      </Tabs>
      {tabIndex === 0 && (
        <Box mt={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" gutterBottom>
              Project Information
            </Typography>
            <Box>
              <Button variant="contained" color="primary" onClick={handleNewProject} sx={{ marginRight: 1 }}>
                New
              </Button>
              <Button variant="contained" color="primary" onClick={handleSave}>
                Save
              </Button>
              <Button variant="contained" color="secondary" onClick={handleDelete} sx={{ marginLeft: 1 }}>
                Delete
              </Button>
            </Box>
          </Box>
          <ProjectFormFields
            project={project}
            companies={companies}
            locations={locations}
            projectTypes={projectTypes}
            handleInputChange={handleInputChange}
            handleCompanyChange={handleCompanyChange}
            handleLocationChange={handleLocationChange}
          />
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Project Assets</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ProjectAssets projectId={projectId} />
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
      {tabIndex === 1 && (
        <Box mt={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" gutterBottom>
              Project Milestones
            </Typography>
            {/* Add any other existing buttons or search fields here */}
          </Box>
          <KanbanBoard projectId={projectId} />
        </Box>
      )}
      {tabIndex === 2 && (
        <Box mt={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" gutterBottom>
              Project Timeline
            </Typography>
            {/* Add any other existing buttons or search fields here */}
          </Box>
          <ProjectTimeline projectId={projectId} />
        </Box>
      )}
      {tabIndex === 3 && (
        <Box mt={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" gutterBottom>
              Project Team
            </Typography>
            {/* Add any other existing buttons or search fields here */}
          </Box>
          <ProjectTeams projectId={projectId} projectName={project.project_name} />
        </Box>
      )}
      {tabIndex === 4 && (
        <Box mt={3}>
          <ProjectDocuments projectId={projectId} />
        </Box>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
      >
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Do you want to save them before creating a new project?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={() => handleCreateNewProject(true)} color="primary">
            Save
          </Button>
          <Button onClick={() => handleCreateNewProject(false)} color="primary">
            Don't Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProjectEdit;